import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import TestimoniosSection from "@/components/TestimoniosSection";
import FAQSection from "@/components/FAQSection";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import {
  Home,
  Hammer,
  RefreshCw,
  Wallet,
  Timer,
  ShieldCheck,
  Handshake,
  Sparkles,
  ArrowRight,
  MessageCircle,
  ClipboardList,
  UserCheck,
  Rocket,
} from "lucide-react";
import { WHATSAPP_LINK, CREDITARIA_URL } from "@/lib/api";

const SOLUCIONES = [
  {
    icon: Home,
    title: "Adquisición",
    desc: "Compra la casa o departamento que has soñado con condiciones a tu medida.",
  },
  {
    icon: Hammer,
    title: "Construcción",
    desc: "Financia la obra desde los cimientos con desembolsos por etapas.",
  },
  {
    icon: RefreshCw,
    title: "Sustitución de hipoteca",
    desc: "Cambia tu crédito actual por uno con mejor tasa, plazo y mensualidad.",
  },
  {
    icon: Wallet,
    title: "Liquidez",
    desc: "Obtén dinero usando tu inmueble como garantía y con mejores tasas del mercado.",
  },
];

const PROCESO = [
  {
    icon: ClipboardList,
    step: "01",
    title: "Regístrate",
    desc: "Déjanos tus datos y responde algunas preguntas sencillas.",
  },
  {
    icon: UserCheck,
    step: "02",
    title: "Precalifica en 3 minutos",
    desc: "Nuestro sistema evalúa al instante y te dice cuánto puedes obtener.",
  },
  {
    icon: Handshake,
    step: "03",
    title: "Te acompañamos",
    desc: "Un asesor experto te guía en cada paso hasta el desembolso.",
  },
  {
    icon: Rocket,
    step: "04",
    title: "Firma y disfruta",
    desc: "Cerramos tu crédito en tiempo récord con condiciones claras.",
  },
];

const BENEFICIOS = [
  {
    icon: Sparkles,
    title: "Comparamos +15 instituciones por ti",
    desc: "Analizamos bancos, sofomes y fondos hipotecarios para conseguirte la mejor tasa, plazo y mensualidad del mercado.",
  },
  {
    icon: ShieldCheck,
    title: "Asesoría y gestoría sin costo",
    desc: "Nuestro acompañamiento profesional es 100% gratis: no te cobramos comisiones ni cuotas por tramitar tu crédito.",
  },
  {
    icon: Timer,
    title: "Precalificación inmediata",
    desc: "Resultado en solo 3 minutos, sin filas ni papeleo. Sabes al momento si calificas y cuánto puedes obtener.",
  },
  {
    icon: Handshake,
    title: "Contigo hasta la firma",
    desc: "Te acompañamos en cada etapa: expediente, avalúo, notaría y firma. Rápido, claro, contigo.",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bf-gridlines opacity-70 pointer-events-none" />
        <div className="absolute -top-20 -right-40 h-[500px] w-[500px] rounded-full bg-[#f05c3e]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#04b0ba]/10 blur-3xl pointer-events-none" />

        <div className="bf-container relative py-14 md:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="bf-chip bf-fade-up" data-testid="hero-chip">
              <span className="h-1.5 w-1.5 rounded-full bg-[#04b0ba]" />
              Crédito hipotecario en Guadalajara · Sin costo
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#333333] bf-fade-up bf-delay-1">
              Tu <span className="text-[#f05c3e]">crédito hipotecario</span>, sin adivinar.<br/>
              <span className="text-[#333333]">Conoce si </span><span className="text-[#f05c3e]">calificas</span> y{" "}
              <span className="text-[#04b0ba]">cuánto</span> te prestamos en{" "}
              <span className="underline decoration-[#f05c3e] decoration-4 underline-offset-4">3 minutos</span>.
            </h1>
            <p className="mt-4 text-base md:text-lg text-[#666666] max-w-xl leading-relaxed bf-fade-up bf-delay-2">
              Broker hipotecario en Guadalajara y Jalisco. Rápido, claro, contigo.
            </p>

            <div className="mt-6 flex flex-wrap gap-3 bf-fade-up bf-delay-3">
              <a
                href={CREDITARIA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                className="bf-btn-primary rounded-full px-7 py-4 font-semibold inline-flex items-center gap-2"
              >
                Precalifícate ahora <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="bf-btn-outline rounded-full px-7 py-4 font-semibold inline-flex items-center gap-2"
              >
                <MessageCircle className="h-4 w-4" /> Hablar por WhatsApp
              </a>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg bf-fade-up bf-delay-4">
              <Stat kpi="3 min" label="Precalificación" />
              <Stat kpi="+15" label="Instituciones comparadas" />
              <Stat kpi="$0" label="Asesoría y gestoría" />
            </div>
          </div>

          <div className="lg:col-span-5 bf-fade-up bf-delay-3">
            <HeroCard />
          </div>
        </div>

        {/* Trust strip */}
        <div className="border-y border-[#e5e7eb] bg-white/60">
          <div className="bf-container py-4 overflow-hidden">
            <div className="flex gap-14 bf-marquee-track whitespace-nowrap text-xs uppercase tracking-[0.25em] text-[#666666]">
              {[...Array(2)].map((_, i) => (
                <React.Fragment key={`marquee-loop-${i}`}>
                  <span>Rápido</span><span>·</span>
                  <span>Claro</span><span>·</span>
                  <span>Contigo</span><span>·</span>
                  <span>Guadalajara</span><span>·</span>
                  <span>Zapopan</span><span>·</span>
                  <span>Tlaquepaque</span><span>·</span>
                  <span>Tlajomulco</span><span>·</span>
                  <span>Puerto Vallarta</span><span>·</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUCIONES */}
      <section id="soluciones" className="py-16" data-testid="soluciones-section">
        <div className="bf-container">
          <div className="max-w-2xl">
            <p className="bf-eyebrow">Nuestras soluciones</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#333333]">
              Un crédito para <span className="text-[#04b0ba]">cada etapa</span> de tu vida.
            </h2>
            <p className="mt-3 text-[#666666]">
              Ya sea que compres, construyas, refinancies o necesites liquidez, tenemos la solución.
            </p>
          </div>

          <div className="mt-10 grid gap-4 grid-cols-2 lg:grid-cols-4">
            {SOLUCIONES.map((s, i) => (
              <SolucionCard key={s.title} solucion={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PROCESO */}
      <section id="proceso" className="py-16 bg-[#333333] text-white relative overflow-hidden" data-testid="proceso-section">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#04b0ba] to-transparent" />
        <div className="bf-container relative">
          <div className="grid lg:grid-cols-2 gap-6 items-end">
            <div>
              <p className="bf-eyebrow" style={{ color: "#f05c3e" }}>Cómo lo hacemos</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl">
                Un proceso <span className="text-[#f05c3e]">simple</span>,{" "}
                de principio a fin.
              </h2>
            </div>
            <p className="text-white/70 max-w-md lg:justify-self-end">
              Combinamos tecnología con acompañamiento humano para que tu crédito sea rápido y sin complicaciones.
            </p>
          </div>

          <div className="mt-10 grid gap-6 grid-cols-2 lg:grid-cols-4">
            {PROCESO.map((p) => (
              <div key={p.step} className="relative">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <p.icon className="h-5 w-5 text-[#f05c3e]" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-[#04b0ba] tracking-widest">{p.step}</div>
                    <h3 className="font-display text-base">{p.title}</h3>
                  </div>
                </div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <a
              href={CREDITARIA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="proceso-cta-button"
              className="bf-btn-primary rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2"
            >
              Empieza tu precalificación <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section id="beneficios" className="py-16" data-testid="beneficios-section">
        <div className="bf-container">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <p className="bf-eyebrow">Beneficios</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#333333]">
                Por qué elegir{" "}
                <span className="text-[#f05c3e]">Brillo Finance</span>.
              </h2>
              <p className="mt-3 text-[#666666] leading-relaxed">
                Cuatro razones que hacen que miles de clientes en Jalisco nos elijan cada mes.
              </p>
              <a
                href={CREDITARIA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="beneficios-cta-button"
                className="mt-5 bf-btn-primary rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2"
              >
                Quiero mi precalificación <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <div className="lg:col-span-7 grid gap-3 sm:grid-cols-2">
              {BENEFICIOS.map((b) => (
                <div
                  key={b.title}
                  className="rounded-xl border border-[#e5e7eb] bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-md hover:border-[#04b0ba]/40"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-[#04b0ba]/10 flex items-center justify-center flex-shrink-0">
                      <b.icon className="h-4 w-4 text-[#04b0ba]" />
                    </div>
                    <h3 className="font-display text-base text-[#333333]">{b.title}</h3>
                  </div>
                  <p className="mt-2 text-sm text-[#666666] leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TestimoniosSection />

      <ContactSection />

      <FAQSection />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

function Stat({ kpi, label }) {
  return (
    <div>
      <div className="font-display text-2xl md:text-3xl text-[#333333]">{kpi}</div>
      <div className="text-xs text-[#666666] uppercase tracking-[0.15em] mt-1">{label}</div>
    </div>
  );
}

function SolucionCard({ solucion, index }) {
  const { icon: Icon, title, desc } = solucion;
  const isAccent = index === 1 || index === 3;
  return (
    <a
      href={CREDITARIA_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`solucion-card-${index}`}
      className="group text-left rounded-xl border border-[#e5e7eb] bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-md hover:border-[#f05c3e]/40 flex flex-col"
    >
      <div className="flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isAccent ? "bg-[#f05c3e]/10" : "bg-[#04b0ba]/10"
          }`}
        >
          <Icon className={`h-5 w-5 ${isAccent ? "text-[#f05c3e]" : "text-[#04b0ba]"}`} />
        </div>
        <h3 className="font-display text-lg text-[#333333]">{title}</h3>
      </div>
      <p className="mt-3 text-sm text-[#666666] leading-relaxed flex-1">{desc}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#333333] group-hover:text-[#f05c3e] transition-colors">
        Precalificar <ArrowRight className="h-3 w-3" />
      </span>
    </a>
  );
}

function HeroCard() {
  return (
    <div className="relative">
      <div className="rounded-3xl border border-[#e5e7eb] bg-white p-8 shadow-[0_20px_60px_-25px_rgba(51,51,51,0.25)]">
        <div className="flex items-center justify-between">
          <div className="bf-chip"><Timer className="h-3.5 w-3.5" /> 3 minutos</div>
          <span className="text-xs text-[#666666]">Precalificación oficial</span>
        </div>
        <p className="mt-6 text-[#666666] text-sm">Simulación estimada</p>
        <div className="mt-1 font-display text-4xl text-[#333333]">
          $2,850,000 <span className="text-lg text-[#04b0ba]">MXN</span>
        </div>
        <p className="mt-1 text-xs text-[#666666]">
          Con base en ingresos de $65,000/mes · Plazo 20 años · Empleado
        </p>

        <div className="mt-6 space-y-3">
          <MiniBar label="Tasa fija estimada" value="9.4%" width="60%" />
          <MiniBar label="Enganche sugerido" value="10%" width="30%" color="#04b0ba" />
          <MiniBar label="Mensualidad aprox." value="$26,800" width="80%" color="#f05c3e" />
        </div>

        <a
          href={CREDITARIA_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-card-cta"
          className="mt-6 w-full bf-btn-primary rounded-xl py-3.5 font-semibold inline-flex items-center justify-center gap-2"
        >
          Calcular mi caso <ArrowRight className="h-4 w-4" />
        </a>
        <p className="mt-3 text-[11px] text-center text-[#666666]">
          Cifras ilustrativas. El resultado real depende de tu perfil.
        </p>
      </div>

      {/* Floating badge */}
      <div className="absolute -top-4 -left-4 rounded-full bg-[#04b0ba] text-white px-4 py-2 text-xs font-semibold shadow-md">
        Sin costo
      </div>
    </div>
  );
}

function MiniBar({ label, value, width, color = "#333333" }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-[#666666]">
        <span>{label}</span>
        <span className="text-[#333333] font-medium">{value}</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-[#f8f9fa] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
